import type { User } from '@repo/domain';
import { isApplicationMode } from '@repo/env-variables';
import { createStore, persist } from '@repo/zustand';

export type AuthStatus = 'unauthenticated' | 'guest' | 'authenticated';
interface AuthUser {
  id: string;
}

type State = {
  accessToken: string | undefined;
  user: AuthUser | undefined;
  pendingConsents: boolean | undefined;
  consentsNeeded: boolean | undefined;
  userConversionEmail: string | undefined;
  isImpersonating: boolean;

  login: (user: User, accessToken: string) => void;
  setAccessToken: (accessToken: string) => void;
  setImpersonating: (impersonating: boolean) => void;
  setPendingConsents: (pending: boolean) => void;
  setConsentsNeeded: (needed: boolean) => void;
  setUserConversionEmail: (email: string | undefined) => void;
  logout: () => void;
};

const createDefaults = (): Pick<
  State,
  'accessToken' | 'user' | 'pendingConsents' | 'consentsNeeded' | 'userConversionEmail' | 'isImpersonating'
> => ({
  accessToken: undefined,
  user: undefined,
  pendingConsents: undefined,
  consentsNeeded: undefined,
  userConversionEmail: undefined,
  isImpersonating: false,
});

export const useAuthStore = createStore<State>({
  name: 'AuthStore',
  isDev: isApplicationMode('dev'),
})(
  persist(
    (set) => ({
      ...createDefaults(),

      login: (user: User, accessToken: string) => {
        set({
          user: { id: user.id },
          accessToken,
        });
      },

      setAccessToken: (accessToken: string) => {
        set({ accessToken });
      },

      setImpersonating: (impersonating: boolean) => {
        set({ isImpersonating: impersonating });
      },

      setPendingConsents: (pending: boolean) => {
        set({ pendingConsents: pending });
      },

      setConsentsNeeded: (needed: boolean) => {
        set({ consentsNeeded: needed });
      },

      setUserConversionEmail: (email: string | undefined) => {
        set({ userConversionEmail: email });
      },

      logout: () => {
        set(createDefaults());
      },
    }),
    {
      name: 'user-storage-v5',
      partialize: ({ accessToken, user, consentsNeeded, userConversionEmail, isImpersonating }) => ({
        accessToken,
        user,
        consentsNeeded,
        userConversionEmail,
        isImpersonating,
      }),
    }
  )
);

export const selectNeedsAuthOrConsent = (state: State): boolean =>
  selectStatus(state) !== 'authenticated' || !!state.pendingConsents || !!state.consentsNeeded;

export const selectStatus = (state: State): AuthStatus => {
  if (!state.accessToken) return 'unauthenticated';
  if (!state.user) return 'guest';
  return 'authenticated';
};
